import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '../auth.service';

@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {
    private authService = inject(AuthService);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const authRequest = this.attachToken(request);

        return next.handle(authRequest).pipe(
            catchError((error: unknown) => {
                if (this.shouldAttemptRefresh(error, authRequest)) {
                    return this.refreshAndRetry(authRequest, next, error as HttpErrorResponse);
                }

                return throwError(() => error);
            })
        );
    }

    private attachToken(request: HttpRequest<any>): HttpRequest<any> {
        if (!this.authService.isLoggedIn()) {
            return request;
        }

        const token = this.authService.getToken();

        if (!token) {
            return request;
        }

        return request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`)
        });
    }

    /**
     * A refresh + single retry is attempted only for a genuine 401 on a request
     * that carried a Bearer token and is not itself an auth endpoint (login /
     * refresh / revoke). The auth-endpoint exclusion is the loop guard: a 401
     * from the refresh call must never trigger another refresh (Issue #6).
     */
    private shouldAttemptRefresh(error: unknown, request: HttpRequest<any>): boolean {
        return (
            error instanceof HttpErrorResponse &&
            error.status === 401 &&
            request.headers.has('Authorization') &&
            !this.isAuthEndpoint(request.url)
        );
    }

    private isAuthEndpoint(url: string): boolean {
        // Covers users/authenticate, users/authenticate/refresh and
        // users/authenticate/revoke.
        return url.includes('users/authenticate');
    }

    private refreshAndRetry(
        request: HttpRequest<any>,
        next: HttpHandler,
        originalError: HttpErrorResponse
    ): Observable<HttpEvent<any>> {
        return this.authService.refreshSession().pipe(
            switchMap(user => {
                // Exactly one retry with the freshly rotated token. Errors from
                // this retried request are handled by the catchError below and
                // are never fed back into shouldAttemptRefresh, so there is no
                // recursion and at most one retry per request.
                const retried = request.clone({
                    headers: request.headers.set('Authorization', `Bearer ${user.token}`)
                });

                return next.handle(retried);
            }),
            catchError((refreshError: unknown) => {
                // Offline-first: a network failure (status 0) during the refresh
                // must not be turned into an auth failure — surfacing it as-is
                // lets the ErrorHandler queue/handle it offline instead of wiping
                // the session and its unsynced changes. Any other failure falls
                // through as the original 401 so the existing logout/redirect
                // fallback runs.
                if (refreshError instanceof HttpErrorResponse && refreshError.status === 0) {
                    return throwError(() => refreshError);
                }

                return throwError(() => originalError);
            })
        );
    }
}
